import React from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Box,
    Typography,
    Divider,
    useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import DarkModeToggle from "../global/DarkModeToggle";
import { TransLang } from "./TransLang";
import { useAuthContext } from "../../auth/useAuthContext";
import { useSnackbar } from "notistack";
import i18next from "i18next";

export default function Navbar() {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const { toggleDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const { user, logout } = useAuthContext();
    const { enqueueSnackbar } = useSnackbar();
    let lng = i18next.language;

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleLogout = async () => {
        try {
            const response = await logout();
            const message = response?.data?.message || "Logged out successfully";
            enqueueSnackbar(message, { variant: "success" });
            navigate("/login");
        } catch (error) {
            console.error(error);
            const errorMessage =
                error?.response?.data?.message || error?.message || "Logout failed";
            enqueueSnackbar(errorMessage, { variant: "error" });
        }
    };

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                borderRadius: 0,
                backgroundColor: theme.palette.primary.main,
                backgroundImage: isDark
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #0F172A 100%)`
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #64B5F6 100%)`,
                color: "#FFFFFF",
                boxShadow: `0 2px 10px 0 rgba(30,136,229,0.35)`,
            }}
        >
            <Toolbar
                sx={{
                    minHeight: 60,
                    px: 2.5,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TransLang />
                    <Box
                        onClick={toggleDarkMode}
                        sx={{
                            color: "#FFFBEB",
                            "&:hover": { bgcolor: "rgba(15,23,42,0.12)" },
                        }}
                    >
                        <DarkModeToggle />
                    </Box>
                </Box>

                <Box sx={{ ml: 1 }}>
                    <IconButton
                        onClick={handleOpen}
                        size="small"
                        sx={{
                            p: 0.3,
                            borderRadius: 999,
                            border: "1px solid rgba(15,23,42,0.25)",
                            bgcolor: "rgba(15,23,42,0.08)",
                            "&:hover": {
                                bgcolor: "rgba(15,23,42,0.16)",
                            },
                        }}
                    >
                        <Avatar
                            src={user?.image?.thumb}
                            sx={{ width: 32, height: 32 }}
                        />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 220,
                                borderRadius: 2,
                                boxShadow: "0 10px 25px rgba(15,23,42,0.25)",
                            },
                        }}
                    >
                        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {user?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {user?.email}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                            <Typography variant="body2" color="text.secondary" onClick={() => navigate('/profile')} sx={{ cursor: 'pointer' }}>
                                {lng === "ar" ? "عرض الملف الشخصي" : "View Profile"}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem
                            onClick={() => {
                                handleLogout();
                                handleClose();
                            }}
                            sx={{
                                py: 1,
                            }}
                        >
                            <LogoutIcon
                                fontSize="small"
                                sx={{ mr: 1, color: theme.palette.error.main }}
                            />
                            <Typography variant="body2">
                                {lng === "ar" ? "تسجيل الخروج" : "Logout"}
                            </Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
}
